import React from "react";
import { Box, Paper, Typography, Chip, Tooltip, IconButton } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { formatDateTime } from "../../../../../utils/formatters/date";
import ExportExcelButton from "../../../../../components/Basic/ExportExcelButton";

const exportColumns = [
  { field: "employeeId", headerName: "Employee ID" },
  { field: "fullName", headerName: "Full Name" },
  { field: "mail", headerName: "Email" },
  {
    field: "loginDate",
    headerName: "Login",
    valueGetter: (r) => formatDateTime(r.loginDate),
  },
  {
    field: "lastActivity",
    headerName: "Last Activity",
    valueGetter: (r) => formatDateTime(r.lastActivity),
  },
  { field: "minutesSinceLastActivity", headerName: "Minutes Idle" },
  { field: "ipAddress", headerName: "IP Address" },
  { field: "userAgent", headerName: "User Agent" },
];

const OnlineUsersWidget = ({ rows, loading, onRefresh }) => {
  const columns = [
    {
      field: "fullName",
      headerName: "Name",
      flex: 1,
      minWidth: 180,
      renderCell: ({ row }) => (
        <Box sx={{ py: 0.5 }}>
          <Typography variant="body2" fontWeight={600}>
            {row.fullName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row.mail}
          </Typography>
        </Box>
      ),
    },
    {
      field: "loginDate",
      headerName: "Login",
      width: 170,
      renderCell: ({ value }) => formatDateTime(value),
    },
    {
      field: "lastActivity",
      headerName: "Last Activity",
      width: 170,
      renderCell: ({ value }) => formatDateTime(value),
    },
    {
      field: "minutesSinceLastActivity",
      headerName: "Idle",
      width: 90,
      renderCell: ({ value }) => (
        <Chip
          size="small"
          label={`${value}m`}
          color={value <= 5 ? "success" : value <= 15 ? "warning" : "default"}
          variant="outlined"
        />
      ),
    },
    { field: "ipAddress", headerName: "IP", width: 130 },
    {
      field: "userAgent",
      headerName: "Device",
      flex: 1,
      minWidth: 200,
      renderCell: ({ value }) => (
        <Tooltip title={value || ""}>
          <Typography
            variant="caption"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "block",
            }}
          >
            {value || "—"}
          </Typography>
        </Tooltip>
      ),
    },
  ];

  return (
    <Paper sx={{ p: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            Online Users
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {rows?.length || 0} active session(s) · auto-refresh every 60s
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <ExportExcelButton
            rows={rows || []}
            columns={exportColumns}
            filename="online_users"
          />
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={onRefresh}>
              <RefreshOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Box sx={{ height: 360 }}>
        <DataGrid
          rows={(rows || []).map((r) => ({ id: r.employeeId, ...r }))}
          columns={columns}
          loading={loading}
          density="compact"
          hideFooterSelectedRowCount
          pageSizeOptions={[10, 25]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          sx={{ border: "none" }}
        />
      </Box>
    </Paper>
  );
};

export default OnlineUsersWidget;
